import React, { useContext, useEffect, useRef, useCallback, useMemo } from "react";
import { LanguageContext } from "../../context/LanguageContext";
import { getText } from "../configure/utils";
import { useAuth } from "../../components/msalAuth/AuthProvider";
import { useEnv } from "../chat/utils";

export default function Chatbox() {
  const { language } = useContext(LanguageContext);
  const { user } = useAuth();
  const { isValid, values, error } = useEnv(["DIALOGFLOW_CX_PROJECT_ID", "DIALOGFLOW_CX_WALK_IN_CENTER_AGENT_ID"]);

  const dfMessengerRef = useRef(null);
  const parameterRef = useRef(false);
  const languageRef = useRef(language || "th");
  const NEW_SESSION = useMemo(
    () => [
      {
        type: "button",
        icon: { type: "chevron_right", color: "#FF9800" },
        mode: "blocking",
        text: getText("df_cx.timeout.button"),
        event: { event: "greeting" },
      },
    ],
    [language]
  );

  const attachEmail = useCallback(() => {
    const email = user?.username;
    if (!email || !dfMessengerRef.current) return;

    dfMessengerRef.current.setQueryParameters({
      parameters: { email: email },
    });
    parameterRef.current = true;
  }, [user?.username]);

  const clearQueryParameters = useCallback(() => {
    if (parameterRef.current && dfMessengerRef.current) {
      dfMessengerRef.current.setQueryParameters({});
      parameterRef.current = false;
    }
  }, []);

  const handleSentRequest = useCallback((event) => {
    const queryInput = event.detail?.data?.requestBody?.queryInput;
    if (queryInput) {
      queryInput.languageCode = languageRef.current;
    }
  }, []);

  const handleSessionExpired = useCallback(() => {
    if (!dfMessengerRef.current) return;

    dfMessengerRef.current.renderCustomText(getText("df_cx.timeout.message"), true);
    dfMessengerRef.current.renderCustomCard(NEW_SESSION);
  }, [NEW_SESSION]);

  const handleClickButton = useCallback(
    (event) => {
      if (!dfMessengerRef.current || !event?.detail) return;
      const messenger = dfMessengerRef.current;

      const { event: evt, text } = event.detail;
      if (evt) {
        messenger.renderCustomText(text, false);
        if (evt === "greeting") {
          attachEmail();
          return;
        } else {
          clearQueryParameters();
          return;
        }
      }
      clearQueryParameters();
      if (text) {
        messenger.renderCustomText(text, false);
        messenger.sendRequest("query", text);
      } else {
        messenger.sendRequest("event", "flow-failed");
      }
    },
    [clearQueryParameters, attachEmail]
  );

  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  useEffect(() => {
    const messenger = document.querySelector("df-messenger");
    if (!messenger) return;
    messenger.startNewSession({ retainHistory: false });
    dfMessengerRef.current = messenger;

    const timeoutId = setTimeout(() => {
      attachEmail();
      messenger.sendRequest("event", "greeting");
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const eventHandlers = {
      "df-request-sent": handleSentRequest,
      "df-user-input-entered": clearQueryParameters,
      "df-chip-clicked": clearQueryParameters,
      "df-session-expired": handleSessionExpired,
      "df-button-clicked": handleClickButton,
    };

    Object.entries(eventHandlers).forEach(([event, handler]) => {
      window.addEventListener(event, handler);
    });

    return () => {
      Object.entries(eventHandlers).forEach(([event, handler]) => {
        window.removeEventListener(event, handler);
      });
    };
  }, [handleSentRequest, clearQueryParameters, handleSessionExpired, handleClickButton]);

  if (!isValid) {
    console.error("WalkinChat configuration is invalid:", error);
    return null;
  }

  return (
    <df-messenger
      project-id={values.DIALOGFLOW_CX_PROJECT_ID}
      agent-id={values.DIALOGFLOW_CX_WALK_IN_CENTER_AGENT_ID}
      allow-feedback="all"
      location="asia-southeast1"
    >
      <df-messenger-chat chat-title="Walk-in Center" chat-subtitle={getText("walkin.subtitle")} placeholder-text={getText("placeholdertext")} />
    </df-messenger>
  );
}
